import { useEffect, useState, useImperativeHandle, Dispatch, SetStateAction } from 'react';
import { Container, Group, TextInput, Button, Select, Stack, ActionIcon } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSearch, IconX, IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { SvgTrash } from '@/components/Common/icon';
import AddUserDialog from '@/pages/NewAdminConsole/components/AddUserDialog';
import MemberEditDialogs from '@/pages/NewAdminConsole/components/MemberEditDialogs';
import { MemberList } from '@/pages/NewAdminConsole/components/MemberManagement/MemberList';
import { BatchDeleteModal } from '@/pages/NewAdminConsole/components/MemberManagement/BatchDeleteModal';
import { usePermissionControl } from '@/pages/NewAdminConsole/hooks/usePermissionControl';
import { MemberData, usePermissionData } from '@/pages/NewAdminConsole/hooks/usePermissionData';

export const MEMBER_LIST_PAGE_SIZE = 20;

interface Props {
  ref: any;
  filteredMembers: MemberData[];
  searchTerm: string;
  selectedDeptId: number | string | null;
  setSearchTerm: Dispatch<string>;
  setSelectedDeptId: Dispatch<SetStateAction<number | string | null>>;
}

export const MemberManagement = ({
  ref,
  filteredMembers,
  searchTerm,
  selectedDeptId,
  setSearchTerm,
  setSelectedDeptId,
}: Props) => {
  const { t } = useTranslation();
  const permissionControl = usePermissionControl();

  const {
    organizationData: orgData,
    roles,
    departments,
    setOrganizationData: setOrgData,
    loadAssignableRoles,
    loadMembersData,
    createUser,
    updateUser,
    updateUserDepartmentAndRole,
    toggleUserStatus,
    resetPassword,
    assignUserToDepartment,
    getUserApiKeys,
    addUserApiKey,
    deleteUserApiKey,
  } = usePermissionData();
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [selectedPermission, setSelectedPermission] = useState<string>('');
  const [permissionDropdownOpened, setPermissionDropdownOpened] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(MEMBER_LIST_PAGE_SIZE);

  // 权限下拉选项数据
  const permissionOptions = [
    { value: '', label: t('adminConsole.newConsole.allPermissions') },
    { value: 'ROLE_COMPANY_OWNER', label: t('adminConsole.newConsole.permissions.companyOwner') },
    { value: 'ROLE_ADMIN', label: t('adminConsole.newConsole.permissions.companyAdmin') },
    { value: 'ROLE_ADMIN_DEPARTMENT', label: t('adminConsole.newConsole.permissions.departmentAdmin') },
    { value: 'ROLE_EDITOR', label: t('adminConsole.newConsole.permissions.editor') },
    { value: 'ROLE_VIEWER', label: t('adminConsole.newConsole.permissions.viewer') },
  ];

  const filterWithPermission = filteredMembers.filter((member) => {
    return selectedPermission === '' || member.roleCode === selectedPermission;
  });

  const totalPage = Math.ceil(filterWithPermission.length / pageSize);
  const filterWithPagination = filterWithPermission.slice((activePage - 1) * pageSize, activePage * pageSize);

  const onPageChange = (page: number) => {
    setActivePage(page);
    setSelectedMembers([]);
  };

  const onPageSizeChange = (size: number) => {
    setPageSize(size);
    setActivePage(1);
    setSelectedMembers([]);
  };

  // Initialize states with translations when component mounts
  useEffect(() => {
    setSelectedDeptId(null);
    setSelectedPermission('');
  }, [t, setSelectedDeptId]);

  // 全局点击监听器，确保权限下拉菜单在点击外部时关闭
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      if (permissionDropdownOpened) {
        const target = event.target as Element;
        // 检查点击是否在权限选择组件外部
        const permissionSelect = document.querySelector('[data-permission-select]');
        if (permissionSelect && !permissionSelect.contains(target)) {
          setPermissionDropdownOpened(false);
        }
      }
    };

    if (permissionDropdownOpened) {
      document.addEventListener('mousedown', handleGlobalClick);
      return () => {
        document.removeEventListener('mousedown', handleGlobalClick);
      };
    }
  }, [permissionDropdownOpened]);
  const [editingMember, setEditingMember] = useState<any>(null);

  // 编辑对话框状态
  const [showEditMemberDialog, setShowEditMemberDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showApiBindingDialog, setShowApiBindingDialog] = useState(false);
  const [showDeptPermissionDialog, setShowDeptPermissionDialog] = useState(false);
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
  // 添加用户对话框状态
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  // 批量删除对话框状态
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);

  // 切换成员选择
  const toggleMemberSelection = (memberId: number | string) => {
    const id = typeof memberId === 'string' ? memberId : memberId.toString();
    setSelectedMembers((prev) => (prev.includes(id) ? prev.filter((prevId) => prevId !== id) : [...prev, id]));
  };

  // 全选/取消全选
  const toggleAllMembers = () => {
    // 判断的对象只能是自己有权限（删除）的用户
    // 先将这部分用户过滤出来
    const filteredMembersHavingPermission = filterWithPagination.filter(permissionControl.canDeleteUser);

    if (selectedMembers.length === filteredMembersHavingPermission.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(filteredMembersHavingPermission.map((m) => m.id.toString()));
    }
  };

  // 清除筛选条件
  const handleClearFilters = () => {
    setSelectedDeptId(null);
    setSelectedPermission('');
    setSearchTerm('');
    notifications.show({
      message: t('adminConsole.newConsole.filterCleared'),
      color: 'blue',
    });
  };

  // 处理成员编辑
  const handleEditMember = (member: any) => {
    setEditingMember(member);
    setShowEditMemberDialog(true);
  };

  // 处理状态设置
  const handleStatusSetting = async (member: any) => {
    setEditingMember(member);
    setShowStatusDialog(true);
  };

  // 处理API绑定
  const handleApiBinding = (member: any) => {
    setEditingMember(member);
    setShowApiBindingDialog(true);
  };

  // 处理部门权限设置
  const handleDeptPermissionSetting = (member: any) => {
    setEditingMember(member);
    setShowDeptPermissionDialog(true);
  };

  // 处理密码重置
  const handlePasswordReset = async (member: any) => {
    setEditingMember(member);
    setShowPasswordResetDialog(true);
  };

  // 更新成员信息
  const updateMember = async (updatedMember: any) => {
    const success = await updateUser(updatedMember.id, updatedMember);
    if (success) {
      setEditingMember(updatedMember);
    }
    return success;
  };

  // 处理添加用户
  const handleAddUser = async (userData: {
    name: string;
    email: string;
    password: string;
    departmentId?: string;
    sysRoleId?: number;
  }) => {
    const success = await createUser(userData);
    if (success) {
      setShowAddUserDialog(false);
    }
    return success;
  };

  // 处理部门路径点击
  const handleDepartmentClick = (_departmentName: string, fullPath: string) => {
    // 根据完整路径查找部门 ID
    const findDepartmentByPath = (nodes: any[], path: string): any => {
      const pathParts = path.split(' > ');
      const targetName = pathParts[pathParts.length - 1];

      const searchNode = (node: any, currentPath: string[]): any => {
        const nodePath = [...currentPath, node.name];
        const pathString = nodePath.join(' > ');

        // 检查是否是目标节点（路径和名称都匹配）
        if (node.name === targetName && pathString === path) {
          return node;
        }

        if (node.children) {
          for (const child of node.children) {
            const result = searchNode(child, nodePath);
            if (result) return result;
          }
        }
        return null;
      };

      for (const node of nodes) {
        const result = searchNode(node, []);
        if (result) return result;
      }
      return null;
    };

    const matchedDepartment = findDepartmentByPath(orgData, fullPath);

    // 设置选中的部门 ID
    if (matchedDepartment?.id) {
      setSelectedDeptId(matchedDepartment.id);
    }

    // 展开路径上的所有父节点
    const pathParts = fullPath.split(' > ');

    const expandParentNodes = (nodes: any[]): any[] => {
      return nodes.map((node) => {
        // 检查节点是否在路径上
        const nodeIndex = pathParts.indexOf(node.name);
        if (nodeIndex !== -1 && nodeIndex < pathParts.length - 1) {
          // 这是路径上的父节点，需要展开
          return {
            ...node,
            expanded: true,
            children: node.children ? expandParentNodes(node.children) : [],
          };
        } else if (node.children) {
          // 递归处理子节点
          return {
            ...node,
            children: expandParentNodes(node.children),
          };
        }
        return node;
      });
    };

    setOrgData(expandParentNodes(orgData));
  };

  // 批量删除成功后的回调
  const handleBatchDeleteSuccess = async () => {
    setSelectedMembers([]);
    // 重新加载成员数据
    await loadMembersData(true);
  };

  useEffect(() => {
    // 用户数据变化时，说明用户点击了左侧的部门，重置页码到第一页
    // 搜索条件变化后，也重置页码到第一页
    // 权限筛选变化后，也重置页码到第一页
    onPageChange(1);
  }, [selectedDeptId, searchTerm, selectedPermission]);

  useImperativeHandle(
    ref,
    () => {
      return {
        setSelectedMembersAsEmpty() {
          console.log('setSelectedMembersAsEmpty');
          setSelectedMembers([]);
        },
      };
    },
    []
  );

  return (
    <Container fluid p={0} h="100vh" style={{ overflow: 'hidden' }}>
      <Stack>
        {/* 搜索和筛选 */}
        <Group justify="space-between">
          <Group>
            <TextInput
              placeholder={t('adminConsole.newConsole.searchMember')}
              leftSection={<IconSearch size={16} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              style={{ flex: 1, maxWidth: 400 }}
              rightSection={
                searchTerm && (
                  <ActionIcon size="sm" variant="subtle" onClick={() => setSearchTerm('')}>
                    <IconX size={14} />
                  </ActionIcon>
                )
              }
            />

            <Select
              placeholder={t('adminConsole.newConsole.permissionFilter')}
              value={selectedPermission}
              onChange={(value) => {
                setSelectedPermission(value || '');
                setPermissionDropdownOpened(false);
              }}
              data={permissionOptions}
              style={{ width: 180 }}
              dropdownOpened={permissionDropdownOpened}
              onDropdownOpen={() => setPermissionDropdownOpened(true)}
              onDropdownClose={() => setPermissionDropdownOpened(false)}
              data-permission-select
              comboboxProps={{
                withinPortal: false,
                transitionProps: { duration: 200, transition: 'fade' },
                middlewares: {
                  flip: true,
                  shift: true,
                },
              }}
              onBlur={() => {
                // 延迟关闭，避免点击选项时立即关闭
                setTimeout(() => {
                  setPermissionDropdownOpened(false);
                }, 100);
              }}
            />

            <Button variant="subtle" size="sm" onClick={handleClearFilters}>
              {t('adminConsole.newConsole.clearFilter')}
            </Button>
          </Group>

          <Group>
            <Button
              size="sm"
              color="red"
              leftSection={<SvgTrash />}
              variant="outline"
              className="filled"
              disabled={selectedMembers.length === 0}
              onClick={() => setShowBatchDeleteModal(true)}
            >
              {t('faqs.action.delete')}
            </Button>
            {import.meta.env.VITE_MODE === 'local' && permissionControl.canCreateUser() && (
              <Button size="sm" leftSection={<IconPlus size={16} />} onClick={() => setShowAddUserDialog(true)}>
                {t('adminConsole.newConsole.addUser.title') || '添加用户'}
              </Button>
            )}
          </Group>
        </Group>

        {/* 成员列表 */}
        <MemberList
          activePage={activePage}
          members={filterWithPagination}
          selectedMembers={selectedMembers}
          totalPage={totalPage}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          onToggleAll={toggleAllMembers}
          onToggleSelection={toggleMemberSelection}
          onEdit={handleEditMember}
          onStatusSetting={handleStatusSetting}
          onApiBinding={handleApiBinding}
          onDeptPermissionSetting={handleDeptPermissionSetting}
          onPasswordReset={handlePasswordReset}
          onDepartmentClick={handleDepartmentClick}
          canEditUser={permissionControl.canEditUser}
          canDeleteUser={permissionControl.canDeleteUser}
          canSetUserStatus={permissionControl.canSetUserStatus}
          canManageApiKeys={permissionControl.canManageApiKeys}
          canAssignUserToDepartment={permissionControl.canAssignUserToDepartment}
          canResetPassword={permissionControl.canResetPassword}
          onPageChange={onPageChange}
        />
      </Stack>

      {/* 成员编辑对话框集合 */}
      <MemberEditDialogs
        editingMember={editingMember}
        showEditMemberDialog={showEditMemberDialog}
        showStatusDialog={showStatusDialog}
        showApiBindingDialog={showApiBindingDialog}
        showDeptPermissionDialog={showDeptPermissionDialog}
        showPasswordResetDialog={showPasswordResetDialog}
        organizationData={orgData}
        roles={roles}
        loadAssignableRoles={loadAssignableRoles}
        onCloseEditMember={() => {
          setShowEditMemberDialog(false);
          setEditingMember(null);
        }}
        onCloseStatus={() => {
          setShowStatusDialog(false);
          setEditingMember(null);
        }}
        onCloseApiBinding={() => {
          setShowApiBindingDialog(false);
          setEditingMember(null);
        }}
        onCloseDeptPermission={() => {
          setShowDeptPermissionDialog(false);
          setEditingMember(null);
        }}
        onClosePasswordReset={() => {
          setShowPasswordResetDialog(false);
          setEditingMember(null);
        }}
        onUpdateMember={updateMember}
        onToggleUserStatus={toggleUserStatus}
        onAssignUserToDepartment={assignUserToDepartment}
        onUpdateUserDepartmentAndRole={updateUserDepartmentAndRole}
        onResetPassword={resetPassword}
        onGetUserApiKeys={getUserApiKeys}
        onAddUserApiKey={addUserApiKey}
        onDeleteUserApiKey={deleteUserApiKey}
      />

      {/* 添加用户对话框 */}
      <AddUserDialog
        opened={showAddUserDialog}
        onClose={() => setShowAddUserDialog(false)}
        onSubmit={handleAddUser}
        departments={departments}
        roles={roles}
        loadAssignableRoles={loadAssignableRoles}
        organizationData={orgData}
      />

      {/* 批量删除对话框 */}
      <BatchDeleteModal
        opened={showBatchDeleteModal}
        onClose={() => setShowBatchDeleteModal(false)}
        selectedMembers={filterWithPagination.filter((m) => selectedMembers.includes(String(m.id)))}
        onSuccess={handleBatchDeleteSuccess}
      />
    </Container>
  );
};
